import { useCallback, useEffect, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { Icon } from '@iconify/react'
import { resolvePublicAppUrl } from '@/lib/publicAppUrl'
import styles from './CampaignSiteInstallModal.module.css'

export type CampaignSiteInstallCopyState = 'copied' | 'error'

type CampaignSiteInstallModalProps = {
  campaignName: string
  campaignId: number
  slug: string | null
  snippet: string
  initialCopyState: CampaignSiteInstallCopyState
  onClose: () => void
}

function isLocalhostAppUrl(appUrl: string): boolean {
  try {
    const hostname = new URL(appUrl).hostname
    return hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '[::1]'
  } catch {
    return false
  }
}

export function CampaignSiteInstallModal({
  campaignName,
  campaignId,
  slug,
  snippet,
  initialCopyState,
  onClose,
}: CampaignSiteInstallModalProps) {
  const previewUrl = useMemo(() => {
    if (!slug) return null
    const appUrl = resolvePublicAppUrl()
    if (!appUrl) return `/campanha/${slug}?embed=1`
    return `${appUrl}/campanha/${slug}?embed=1`
  }, [slug])

  const appUrlWarning = useMemo(() => {
    const appUrl = resolvePublicAppUrl()
    if (!appUrl) {
      return 'Defina VITE_PUBLIC_APP_URL no ambiente de produção para gerar um snippet com a URL correta do app.'
    }
    if (isLocalhostAppUrl(appUrl)) {
      return 'O snippet usa localhost como URL do app. Ele só funcionará em testes na sua máquina; em produção, gere o código com o painel no domínio público ou configure VITE_PUBLIC_APP_URL.'
    }
    return null
  }, [])

  const copyAgain = useCallback(async (): Promise<CampaignSiteInstallCopyState> => {
    try {
      await navigator.clipboard.writeText(snippet)
      return 'copied'
    } catch {
      return 'error'
    }
  }, [snippet])

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose()
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [onClose])

  return createPortal(
    <div className={styles.overlay} role="presentation" onClick={onClose}>
      <div
        className={styles.dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby="campaign-site-install-title"
        onClick={(event) => event.stopPropagation()}
      >
        <header className={styles.header}>
          <div className={styles.headerIcon} aria-hidden>
            <Icon icon="mdi:web-plus" />
          </div>
          <div>
            <h2 id="campaign-site-install-title" className={styles.title}>
              Instalar no meu site
            </h2>
            <p className={styles.subtitle}>
              Campanha <strong>{campaignName}</strong> — ID <code>{campaignId}</code>
              {slug ? (
                <>
                  {' '}
                  (slug atual: <code>{slug}</code>)
                </>
              ) : null}
            </p>
          </div>
          <button type="button" className={styles.closeButton} onClick={onClose} aria-label="Fechar">
            <Icon icon="mdi:close" />
          </button>
        </header>

        <div className={styles.body}>
          {appUrlWarning ? (
            <div className={`${styles.alert} ${styles.alertWarning}`} role="note">
              <Icon icon="mdi:information-outline" aria-hidden />
              <div>
                <strong>Atenção à URL do app</strong>
                <p>{appUrlWarning}</p>
              </div>
            </div>
          ) : null}

          {!slug ? (
            <div className={`${styles.alert} ${styles.alertWarning}`} role="note">
              <Icon icon="mdi:alert-circle-outline" aria-hidden />
              <div>
                <strong>Slug da campanha pendente</strong>
                <p>
                  O embed só funcionará no site externo depois que a campanha tiver um slug válido
                  configurado na edição. O código já pode ser colado — ele usará o slug atual assim que
                  estiver disponível.
                </p>
              </div>
            </div>
          ) : null}

          <div
            className={`${styles.alert} ${initialCopyState === 'error' ? styles.alertWarning : styles.alertSuccess}`}
            role="status"
          >
            <Icon
              icon={
                initialCopyState === 'error'
                  ? 'mdi:alert-circle-outline'
                  : 'mdi:check-circle-outline'
              }
              aria-hidden
            />
            <div>
              {initialCopyState === 'error' ? (
                <>
                  <strong>Não foi possível copiar automaticamente</strong>
                  <p>
                    Selecione o código abaixo e copie manualmente (Ctrl+C). O painel precisa estar em
                    HTTPS e o navegador deve permitir acesso à área de transferência.
                  </p>
                </>
              ) : (
                <>
                  <strong>Script copiado para a área de transferência</strong>
                  <p>
                    Cole o código no HTML do seu site (página, artigo ou bloco HTML do construtor) onde a
                    campanha deve aparecer.
                  </p>
                </>
              )}
            </div>
          </div>

          <section className={styles.section}>
            <h3>Como instalar</h3>
            <ol className={styles.steps}>
              <li>Abra o editor do seu site (WordPress, Wix, HTML próprio, etc.).</li>
              <li>Localize a página em que a campanha deve aparecer.</li>
              <li>Insira um bloco ou seção de <strong>código HTML personalizado</strong>.</li>
              <li>Cole o script completo (div + script) e publique ou salve a página.</li>
              <li>
                A página pública da campanha será exibida no seu domínio, com compra e layout iguais ao
                visualizar no sistema.
              </li>
            </ol>
          </section>

          <section className={styles.section}>
            <h3>Dicas importantes</h3>
            <ul className={styles.tips}>
              <li>
                Use a largura total da coluna de conteúdo; o embed se adapta à largura do container.
              </li>
              <li>
                O código usa o <strong>ID da campanha</strong>, que não muda. Alterações de slug, nome ou
                demais dados no painel continuam valendo sem precisar recolher o script no site.
              </li>
              <li>
                Se a campanha tiver <strong>URL externa de página</strong> configurada na edição, ela não
                altera este embed — o iframe usa sempre a página padrão hospedada na plataforma.
              </li>
              <li>
                Links de afiliado (<code>?af=</code>) na URL do seu site são repassados automaticamente para
                a campanha embutida.
              </li>
              <li>
                {previewUrl ? (
                  <>
                    Para testar antes de publicar, abra a prévia:{' '}
                    <a href={previewUrl} target="_blank" rel="noopener noreferrer">
                      abrir página em modo incorporação
                    </a>
                  </>
                ) : (
                  <>
                    Configure um slug na campanha para habilitar o link de prévia da página em modo
                    incorporação.
                  </>
                )}
              </li>
            </ul>
          </section>

          <section className={styles.section}>
            <div className={styles.codeHeader}>
              <h3>Código para colar</h3>
              <button
                type="button"
                className={styles.copyAgainButton}
                onClick={() => {
                  void copyAgain()
                }}
              >
                <Icon icon="mdi:content-copy" aria-hidden />
                Copiar novamente
              </button>
            </div>
            <textarea
              className={styles.codeArea}
              readOnly
              value={snippet}
              rows={12}
              spellCheck={false}
              aria-label="Código de incorporação da campanha"
              onFocus={(event) => event.currentTarget.select()}
            />
          </section>
        </div>

        <footer className={styles.footer}>
          <button type="button" className={styles.primaryButton} onClick={onClose}>
            Entendi
          </button>
        </footer>
      </div>
    </div>,
    document.body,
  )
}
